import styled from 'styled-components'

import { Card as UiCard } from '@/components/ui/card'

import { NAVBAR_HEIGHT, STICKY_TOP, TAB_HEADER_HEIGHT } from './tab-card.constants'

export const Card = styled(UiCard)<{ $fill?: boolean }>(({ theme, $fill }) => ({
  border: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  borderRadius: theme.border.radius['12'],
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  gap: theme.space['16'],
  height: 'auto',
  // Note: the ui Card clips its content, which would stop sticky group headers from sticking
  overflow: 'visible',
  // Note: a table tab fills the content area and lets the table scroll inside it; the table's own
  // card padding already spaces it from the title, so no extra gap
  ...($fill && { flex: 1, flexShrink: 1, gap: 0, minHeight: 0 }),
}))

// Note: sticks at the same level as the side nav's top border so the title and actions stay
// visible; the vertical padding is cancelled by the negative margin, so the layout is unchanged
// while the header is not stuck. While stuck, a page-coloured strip fills the gap between the
// navbar and the header so scrolled content does not show through, and the header takes the look of
// the card's top (border + rounded top corners) spanning the full card width.
// Table tabs scroll inside their own area, so there the header stays in the flow.
export const Header = styled.div<{ $sticky: boolean }>(({ theme, $sticky }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  // Note: reserves the height of a small action button so the card does not jump when it appears
  minHeight: theme.space['32'],
  ...($sticky && {
    // Note: `&&` raises specificity so this wins over the base `display: none` whatever the key order
    '@container scroll-state(stuck: top)': {
      '&&::after, &&::before': {
        display: 'block',
      },
    },
    '&::after': {
      background: theme.colors.white,
      // Note: spans only the card padding (not its border) and draws just the top edge, so the card's
      // own side borders stay visible and nothing is painted past the Body's `overflow: clip` edge
      borderRadius: `${theme.border.radius['12']} ${theme.border.radius['12']} 0 0`,
      borderTop: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
      bottom: 0,
      content: '""',
      display: 'none',
      left: `calc(-1 * ${theme.space['20']})`,
      position: 'absolute',
      right: `calc(-1 * ${theme.space['20']})`,
      top: 0,
      zIndex: -1,
    },
    '&::before': {
      background: theme.colors.gray100,
      // Note: reaches down by the corner radius, behind the rounded border layer, so the card's own
      // square corner does not show outside the rounded one
      bottom: `calc(100% - ${theme.border.radius['12']})`,
      content: '""',
      display: 'none',
      height: `calc(${STICKY_TOP} - ${NAVBAR_HEIGHT} + ${theme.border.radius['12']})`,
      left: `calc(-1 * (${theme.space['20']} + ${theme.border.size['1']}))`,
      position: 'absolute',
      right: `calc(-1 * (${theme.space['20']} + ${theme.border.size['1']}))`,
      zIndex: -2,
    },
    boxSizing: 'border-box',
    // Note: scroll-state is newer than the csstype union
    containerType: 'scroll-state' as never,
    height: TAB_HEADER_HEIGHT,
    margin: '-0.8rem 0',
    padding: '0.8rem 0',
    position: 'sticky',
    top: STICKY_TOP,
    zIndex: 2,
  }),
}))

export const TitleGroup = styled.div(({ theme }) => ({
  alignItems: 'baseline',
  display: 'flex',
  gap: theme.space['8'],
}))

export const Actions = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['8'],
  marginLeft: 'auto',
}))
